import axios from '@axios'

const BASE = '/ai'
const IS_DEV = !process.env.VUE_APP_ENVIRONMENT || process.env.VUE_APP_ENVIRONMENT === 'development'
const MOCK_DELAY = 300

function mockResponse(data) {
  return new Promise(resolve => {
    setTimeout(() => resolve({ data: { data } }), MOCK_DELAY)
  })
}

// ─── Mock: POST /ai/soap-notes ───
const mockSoapNotes = {
  subjective: 'Pasien datang dengan keluhan nyeri kepala sejak 3 hari yang lalu, memberat saat beraktivitas. Disertai mual tanpa muntah. Riwayat hipertensi (+) tidak terkontrol.',
  objective: 'KU: Tampak sakit sedang, Kesadaran: Compos mentis\nTD: 150/90 mmHg, N: 88x/mnt, S: 36.5°C, RR: 20x/mnt\nKepala: Normocephal, nyeri tekan temporal bilateral (+)\nLeher: KGB tidak membesar\nThorax: Cor/Pulmo dalam batas normal\nAbdomen: Supel, BU (+) normal',
  assessment: 'Cephalgia tension type dengan hipertensi grade I. Diagnosis banding: migraine tanpa aura.',
  plan_obat: 'Paracetamol 500mg 3x1 tab, Amlodipin 5mg 1x1 tab.',
  plan_non_obat: 'Edukasi modifikasi gaya hidup, diet rendah garam. Kontrol 1 minggu.',
}

// ─── Mock: POST /ai/drug-check ───
const mockDrugCheck = {
  severity: 'moderate',
  interactions: [
    {
      drug_a: 'Amlodipin 5mg',
      drug_b: 'Simvastatin 20mg',
      severity: 'moderate',
      description: 'Amlodipin dapat meningkatkan kadar Simvastatin dalam darah, meningkatkan risiko efek samping otot (miopati/rhabdomiolisis).',
      recommendation: 'Batasi dosis Simvastatin maksimal 20mg/hari jika dikombinasi dengan Amlodipin. Monitor gejala nyeri otot.',
    },
    {
      drug_a: 'Paracetamol 500mg',
      drug_b: 'Amlodipin 5mg',
      severity: 'low',
      description: 'Tidak ada interaksi signifikan yang diketahui.',
      recommendation: 'Kombinasi aman digunakan.',
    },
  ],
  allergy_warnings: [
    {
      drug: 'Aspirin',
      allergy: 'Aspirin',
      severity: 'high',
      description: 'Pasien memiliki riwayat alergi terhadap Aspirin. Hindari pemberian obat golongan NSAID jika memungkinkan.',
    },
  ],
  summary: 'Ditemukan 1 interaksi sedang (Amlodipin-Simvastatin) dan 1 peringatan alergi (Aspirin). Perhatikan dosis Simvastatin dan hindari NSAID.',
}

// ─── Mock: POST /ai/icd10-suggest ───
const mockIcd10Suggest = {
  suggestions: [
    {
      code: 'R51',
      description: 'Headache — Sakit Kepala',
      confidence: 0.92,
      reason: 'Keluhan utama nyeri kepala tanpa tanda bahaya',
    },
    {
      code: 'I10',
      description: 'Essential (primary) hypertension — Hipertensi Primer',
      confidence: 0.85,
      reason: 'TD 150/90 mmHg, riwayat hipertensi',
    },
    {
      code: 'G43.9',
      description: 'Migraine, unspecified — Migrain',
      confidence: 0.60,
      reason: 'Diagnosis banding berdasarkan pola keluhan',
    },
  ],
}

// ─── Mock: POST /ai/drug-suggest ───
const mockDrugSuggest = {
  suggestions: [
    {
      drug_name: 'Paracetamol 500mg',
      generic_name: 'Paracetamol',
      dosage: '500mg',
      frequency: '3 x 1 setelah makan',
      quantity: 10,
      duration_days: 3,
      indication: 'Analgesik antipiretik untuk nyeri kepala dan demam',
      matched_product: {
        id: 101,
        nama_produk: 'Paracetamol Tab 500mg',
        ukuran: '500mg',
        harga_satuan: 500,
        harga_satuan_produsen: 350,
        all_stock: 250,
        id_satuan: { id: 1, deskripsi: 'Tablet' },
        golongan: [],
      },
      alternatives: [
        {
          id: 102,
          nama_produk: 'Sanmol Tab 500mg',
          ukuran: '500mg',
          harga_satuan: 800,
          harga_satuan_produsen: 600,
          all_stock: 120,
          id_satuan: { id: 1, deskripsi: 'Tablet' },
          golongan: [],
        },
      ],
      aturan_pakai: { value: 15, label: '3 x 1 setelah makan' },
    },
    {
      drug_name: 'Amlodipin 5mg',
      generic_name: 'Amlodipin',
      dosage: '5mg',
      frequency: '1 x 1',
      quantity: 10,
      duration_days: 10,
      indication: 'Antihipertensi untuk kontrol tekanan darah',
      matched_product: {
        id: 201,
        nama_produk: 'Amlodipin Tab 5mg',
        ukuran: '5mg',
        harga_satuan: 600,
        harga_satuan_produsen: 400,
        all_stock: 180,
        id_satuan: { id: 1, deskripsi: 'Tablet' },
        golongan: [],
      },
      alternatives: [],
      aturan_pakai: { value: 20, label: '1 x 1' },
    },
    {
      drug_name: 'Omeprazole 20mg',
      generic_name: 'Omeprazole',
      dosage: '20mg',
      frequency: '1 x 1 sebelum makan',
      quantity: 10,
      duration_days: 7,
      indication: 'Proton pump inhibitor untuk proteksi lambung',
      matched_product: {
        id: 301,
        nama_produk: 'Omeprazole Cap 20mg',
        ukuran: '20mg',
        harga_satuan: 1200,
        harga_satuan_produsen: 900,
        all_stock: 90,
        id_satuan: { id: 2, deskripsi: 'Kapsul' },
        golongan: [],
      },
      alternatives: [],
      aturan_pakai: { value: 25, label: '1 x 1 sebelum makan' },
    },
  ],
  unmatched: [
    {
      drug_name: 'Vitamin B Complex',
      generic_name: 'Vitamin B Complex',
      frequency: '1 x 1',
      quantity: 10,
      indication: 'Suplemen vitamin untuk daya tahan tubuh',
      reason: 'Tidak ditemukan di inventaris',
    },
  ],
  free_text_fallback: 'Paracetamol 500mg 3x1 setelah makan (10 tab) - analgesik antipiretik\nAmlodipin 5mg 1x1 (10 tab) - antihipertensi\nOmeprazole 20mg 1x1 sebelum makan (10 cap) - proteksi lambung\nVitamin B Complex 1x1 (10 tab) - suplemen vitamin',
}

// ─── Mock: POST /ai/visit-completeness ───
// All applicable fields set to missing so every "Terapkan" button can be tested
const mockVisitCompleteness = {
  completeness_score: 25,
  status: 'critical_missing',
  historical_count: 4,
  missing_fields: ['terapiObat', 'terapiNonObat', 'kdTacc', 'kdSadar', 'kdPrognosa', 'alergiObat', 'kdDokter'],
  field_status: {
    terapiObat: 'missing',
    terapiNonObat: 'missing',
    kdTacc: 'missing',
    kdSadar: 'missing',
    kdPrognosa: 'missing',
    alergiObat: 'missing',
    kdDokter: 'missing',
  },
  suggestions: [
    {
      field: 'terapiObat',
      source: 'historical',
      current_value: '',
      suggested_value: 'Paracetamol 500mg 3x1, Amlodipin 5mg 1x1',
      reason: 'Berdasarkan 3 kunjungan terakhir dengan diagnosis serupa',
    },
    {
      field: 'terapiNonObat',
      source: 'clinical',
      current_value: '',
      suggested_value: 'Edukasi diet rendah garam, olahraga teratur 30 menit/hari, kontrol tekanan darah rutin',
      reason: 'Rekomendasi standar untuk pasien hipertensi',
    },
    {
      field: 'kdTacc',
      source: 'clinical',
      current_value: '',
      suggested_value: '1',
      reason: 'Time-based: keluhan akut < 3 hari',
    },
    {
      field: 'kdSadar',
      source: 'historical',
      current_value: '',
      suggested_value: '01',
      reason: 'Riwayat kunjungan sebelumnya: Compos Mentis',
    },
    {
      field: 'kdPrognosa',
      source: 'clinical',
      current_value: '',
      suggested_value: '02',
      reason: 'Prognosa dubia ad bonam berdasarkan kondisi klinis saat ini',
    },
    {
      field: 'alergiObat',
      source: 'historical',
      current_value: '',
      suggested_value: 'Aspirin',
      reason: 'Tercatat di riwayat PCare kunjungan sebelumnya',
    },
    {
      field: 'kdDokter',
      source: 'historical',
      current_value: '',
      suggested_value: '12345',
      reason: 'Kode dokter BPJS dari data pendaftaran',
    },
  ],
  notes: 'Skor kelengkapan 25% — semua field kritis belum terisi. Sinkronisasi PCare akan gagal jika field wajib belum dilengkapi.',
}

export default {
  soapNotes: (data) => IS_DEV ? mockResponse(mockSoapNotes) : axios.post(`${BASE}/soap-notes`, data),
  drugCheck: (data) => IS_DEV ? mockResponse(mockDrugCheck) : axios.post(`${BASE}/drug-check`, data),
  icd10Suggest: (data) => IS_DEV ? mockResponse(mockIcd10Suggest) : axios.post(`${BASE}/icd10-suggest`, data),
  drugSuggest: (data) => IS_DEV ? mockResponse(mockDrugSuggest) : axios.post(`${BASE}/drug-suggest`, data),
  visitCompleteness: (data) => IS_DEV ? mockResponse(mockVisitCompleteness) : axios.post(`${BASE}/visit-completeness`, data),
  getCredits: () => axios.get(`${BASE}/credits`),
}
